import React, { useCallback, useMemo, useRef, useState } from 'react';
import { BaseButton } from '@/components/uiKit/Button';
import { BaseTabs } from '@/components';
import { NotificationMethodsTable } from '@/components/pages/SettingsPage/MainContent/UserManagement/NotificationMethods/NotificationMethodsTable';
import { AssetsAllocationTable } from '@/components/pages/SettingsPage/MainContent/UserManagement/AssetsAllocation/AssetsAllocationTable';
import { UserDetails } from '@/components/pages/SettingsPage/MainContent/UserManagement/UserDetails/UserDetails';
import { useAppFilters, useToast } from '@/hooks';
import { useUserData } from '@/components/pages/SettingsPage/MainContent/shared/useUserData';
import { useParams } from 'react-router-dom';
import {
  ConfirmationResult,
  confirmDialog,
} from '@/components/pages/SettingsPage/MainContent/NotificationPreferences/confirmDialog';

const enum USER_PREFERENCES_TABS {
  USER_DETAILS = 'user-details',
  FAULT_PERMISSION = 'fault-permission',
  NOTIFICATION_METHODS = 'notification-methods',
  ASSETS_ALLOCATIONS = 'assets-allocations',
}

export interface UserPreferencesRef {
  triggerSave: () => void;
}

export const UserPreferences = () => {
  const { tab } = useAppFilters();
  const filters = useAppFilters();
  const { userId: userIdParam } = useParams();
  const { deleteUser } = useUserData();

  const userDetailsRef = useRef<UserPreferencesRef>(null);
  const assetsAllocationRef = useRef<UserPreferencesRef>(null);
  const notificationMethodsRef = useRef<UserPreferencesRef>(null);

  const [isNotificationMethodsTableDirty, setIsNotificationMethodsTableDirty] = useState(false);
  const [isAssetsAllocationTableDirty, setIsAssetsAllocationTableDirty] = useState(false);

  const handleSaveChanges = useCallback(() => {
    [userDetailsRef, assetsAllocationRef, notificationMethodsRef].forEach((ref) => {
      ref.current?.triggerSave();
    });
  }, []);

  const handleDeleteUser = useCallback(() => {
    deleteUser(userIdParam);
  }, []);

  const userPreferencesTabs = useMemo(() => {
    return [
      { id: USER_PREFERENCES_TABS.USER_DETAILS, title: 'User Details', element: <UserDetails ref={userDetailsRef} /> },
      { id: USER_PREFERENCES_TABS.FAULT_PERMISSION, title: 'Fault Permission', disabled: true },
      {
        id: USER_PREFERENCES_TABS.NOTIFICATION_METHODS,
        title: 'Notification Methods',
        element: (
          <NotificationMethodsTable ref={notificationMethodsRef} setIsDirty={setIsNotificationMethodsTableDirty} />
        ),
      },
      {
        id: USER_PREFERENCES_TABS.ASSETS_ALLOCATIONS,
        title: 'Assets Allocation',
        element: <AssetsAllocationTable ref={assetsAllocationRef} setIsDirty={setIsAssetsAllocationTableDirty} />,
      },
    ];
  }, []);

  const renderRightPane = useCallback(() => {
    return (
      <div className="flex items-center min-w-full justify-end pl-6 gap-6">
        {tab === USER_PREFERENCES_TABS.USER_DETAILS ? (
          <BaseButton
            variant="outline"
            size="xl"
            onClick={handleDeleteUser}
            className="ring-[#CC0000] hover:ring-[#FF4D4D] text-[#CC0000] hover:text-[#FF4D4D]"
          >
            Delete User
          </BaseButton>
        ) : null}

        <BaseButton variant="filled" size="xl" onClick={handleSaveChanges}>
          Save Changes
        </BaseButton>
      </div>
    );
  }, [handleSaveChanges, tab]);

  const onTabClick = async (tabId: string) => {
    if (isNotificationMethodsTableDirty || isAssetsAllocationTableDirty) {
      const confirmDialogResult = await confirmDialog();

      switch (confirmDialogResult) {
        case ConfirmationResult.SAVE_AND_EXIT:
          handleSaveChanges();
          filters.setFilters({ ['tab']: tabId });
          break;
        case ConfirmationResult.DONT_SAVE:
          setIsNotificationMethodsTableDirty(false);
          setIsAssetsAllocationTableDirty(false);

          filters.setFilters({ ['tab']: tabId });
          break;
        case ConfirmationResult.CANCEL:
          filters.setFilters({ ['tab']: tab });
          break;
      }
    } else {
      filters.setFilters({ ['tab']: tabId });
    }
  };

  return (
    <div className="flex flex-col h-full gap-4">
      <div className="flex flex-row w-full justify-between gap-4 items-start">
        <div className="flex flex-row w-full">
          <BaseTabs
            tabs={userPreferencesTabs}
            defaultValue={userPreferencesTabs[0].id}
            hideTabTriggers={false}
            renderRightPane={renderRightPane}
            onClick={onTabClick}
            confirmBeforeChange
          />
        </div>
      </div>
    </div>
  );
};
